/*
eWorldES Proprietary Software
Copyright (C) 2025 eWorldES. All rights reserved.

This software is proprietary to eWorldES and is protected by copyright laws and international treaties.
You may not use, copy, modify, distribute, or disclose this software without explicit written permission from eWorldES.
Any unauthorized use or reproduction of this software is strictly prohibited and may result in legal action.
*/

import { useMemo } from "react";
import { Controller } from "react-hook-form";
import { InputFieldProps } from "../../types/InputProps";
import { Box } from "../Box";
import { Chip } from "../Chip";
import { Close } from "@mui/icons-material";
import { InputAdornment } from "../InputAdornment";
import { InputLabel } from "../InputLabel";
import { MenuItem } from "../Menu";
import { Select } from "../Select";
import FormControl from "@mui/material/FormControl";
import InputWrapper from "./InputWrapper";

const FormMultiSelect = (props: InputFieldProps) => {
  const {
    name,
    control,
    label,
    info,
    hint,
    meta = { multiple: true, isChip: true }, // Default to multiple select with chips
    selectKeys,
    options,
    validations = [],
  } = props;

  const memoizedOptions = useMemo(() => options, [options]);

  const showEndAdornment = (value: any) => {
    return value && Array.isArray(value) && value.length > 0;
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={[] as any} // Default empty array for multiselect
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <InputWrapper info={info} hint={hint} error={error}>
          <FormControl
            fullWidth
            required={validations.length > 0}
            error={!!error}
          >
            <InputLabel>{label}</InputLabel>
            <Select
              labelId={`${name}-multiselect`}
              aria-label={`${name}-label ${value}`}
              multiple
              required={validations.length > 0}
              label={label}
              style={{ height: "55px" }}
              onChange={(event) => onChange(event.target.value)}
              value={value || []}
              endAdornment={
                showEndAdornment(value) ? (
                  <InputAdornment position="end" sx={{ mr: 3.4 }}>
                    <Close
                      onClick={() => onChange([])}
                      sx={{ padding: 0.5, cursor: "pointer" }}
                      aria-label="Clear Selection"
                    />
                  </InputAdornment>
                ) : null
              }
              renderValue={(selected: any) => (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                    width: "fit-content",
                  }}
                  onMouseDown={(event) => event.stopPropagation()}
                >
                  {selected.map((fieldValue: any) => (
                    <Chip
                      key={fieldValue}
                      title={name}
                      label={fieldValue}
                      onDelete={() => {
                        onChange(
                          value.filter((chip: string) => chip !== fieldValue)
                        );
                      }}
                    />
                  ))}
                </Box>
              )}
              {...meta}
            >
              {memoizedOptions &&
                memoizedOptions.map((option) => {
                  const key = selectKeys?.codeKey ?? "value";
                  const labelKey = selectKeys?.labelKey ?? "desc";
                  return (
                    <MenuItem
                      key={option[key as keyof typeof option]}
                      value={option[key as keyof typeof option]}
                    >
                      {option[labelKey as keyof typeof option]}
                    </MenuItem>
                  );
                })}
            </Select>
          </FormControl>
        </InputWrapper>
      )}
    />
  );
};

export default FormMultiSelect;